import React, { useEffect, useRef, useCallback } from 'react';
import {
  Button,
  Input,
  Spin,
  Alert,
  Upload,
  Tag,
} from 'antd';
import { SendOutlined, PaperClipOutlined } from '@ant-design/icons';
import { useChatStore } from '../../store/chat';
import { useTabStore } from '../../store/tabs';
import { Workspace, TabState } from '../../types';
import { ChatMessage } from './ChatMessage';
import { WorkspaceTools } from './WorkspaceTools';
import { CommonActions } from './CommonActions';
import { ModelSelector } from './ModelSelector';
import { ErrorBoundary } from '../ErrorBoundary';
import { ThemeAwareLogo } from '../common/ThemeAwareLogo';

interface ChatInterfaceProps {
  workspace?: Workspace;
  initialState?: TabState;
  tabId?: string;
}

export const ChatInterface: React.FC<ChatInterfaceProps> = ({
  workspace,
  initialState,
  tabId
}) => {
  const {
    currentSession,
    isStreaming,
    error,
    messageQueue,
    createSession,
    restoreSession,
    sendMessage,
    queueMessage,
    setError,
    updateSettings,
  } = useChatStore();

  // Use the AI name assigned to this workspace instance
  const aiName = workspace?.aiName || 'AI';


  const { updateTabState, markTabDirty, activeTabId } = useTabStore();

  // const navigate = useNavigate(); // Not needed for tab-based interface

  const [input, setInput] = React.useState('');
  const [messageHistory, setMessageHistory] = React.useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = React.useState(-1);
  const [uploadedFiles, setUploadedFiles] = React.useState<File[]>([]);
  const [isUploading, setIsUploading] = React.useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Configure chat settings when workspace changes
  useEffect(() => {
    if (workspace && !initialState) {

      // Apply workspace settings to chat
      updateSettings({
        workspaceId: workspace.id,
        provider: (workspace.settings as any).preferredProvider || 'openai',
        model: (workspace.settings as any).preferredModel || 'gpt-4o-mini',
        temperature: workspace.temperature || 0.7,
        maxTokens: workspace.settings.maxTokens || 4096,
        domain: workspace.settings.domain || 'general',
        costSensitivity: workspace.settings.costSensitivity || 'medium',
        dataSensitivity: workspace.settings.dataSensitivity || 'internal',
        mcpServers: workspace.allowedMcpServers || [],
        aiName: workspace.aiName, // Pass the AI name from this workspace instance
      });

      // Create a new session with the workspace context and trigger Hello message
      createSession(workspace.contextPrompt, true);
    } else if (workspace && initialState) {
      // Only update workspace-specific settings when restoring - DO NOT create new session
      updateSettings({
        workspaceId: workspace.id,
        mcpServers: workspace.allowedMcpServers || [],
        aiName: workspace.aiName, // Pass the AI name from this workspace instance
        provider: (workspace.settings as any).preferredProvider || 'openai',
        model: (workspace.settings as any).preferredModel || 'gpt-4o-mini',
        temperature: workspace.temperature || 0.7,
        maxTokens: workspace.settings.maxTokens || 4096,
        domain: workspace.settings.domain || 'general',
        costSensitivity: workspace.settings.costSensitivity || 'medium',
        dataSensitivity: workspace.settings.dataSensitivity || 'internal',
      });
    }
  }, [workspace, initialState, updateSettings, createSession]);

  // Load initial state if provided
  useEffect(() => {
    if (initialState && initialState.sessionId && initialState.messages && initialState.messages.length > 0) {
      // Restore the chat session from saved state
      restoreSession({
        sessionId: initialState.sessionId,
        messages: initialState.messages,
        settings: initialState.settings
      });
    }
  }, [initialState, restoreSession]);

  // Create initial session (fallback)
  useEffect(() => {
    if (!currentSession && !workspace) {
      createSession();
    }
  }, [currentSession, workspace, createSession]);

  // Persist state changes to tab store (debounced, but immediate for assistant messages)
  useEffect(() => {
    if (!tabId || !currentSession) return;

    // Check if the last message is an assistant message being updated
    const lastMessage = currentSession.messages[currentSession.messages.length - 1];
    const isAssistantMessageUpdate = lastMessage?.role === 'assistant' && lastMessage?.content;

    // Use shorter delay for assistant message updates to ensure content is saved
    const delay = isAssistantMessageUpdate ? 100 : 1000;

    const timeoutId = setTimeout(() => {
      // Check if component is still mounted and session still exists
      const currentState = useChatStore.getState();
      if (!currentState.currentSession || currentState.currentSession.id !== currentSession.id) {
        return;
      }

      const state = {
        sessionId: currentSession.id,
        messages: currentSession.messages,
        settings: {
          ...currentState.settings,
          ...currentSession.metadata
        },
        timestamp: Date.now(),
      };
      updateTabState(tabId, state);
    }, delay);

    return () => clearTimeout(timeoutId);
  }, [tabId, currentSession?.id, currentSession?.messages?.length, currentSession?.messages?.[currentSession?.messages?.length - 1]?.content, updateTabState]);

  // Auto-scroll to bottom when new messages arrive (with safety check)
  useEffect(() => {
    if (!isMountedRef.current) return;

    const scrollElement = messagesEndRef.current;
    if (scrollElement) {
      // Use requestAnimationFrame to ensure DOM is updated
      const rafId = requestAnimationFrame(() => {
        if (isMountedRef.current && messagesEndRef.current) {
          scrollElement.scrollIntoView({ behavior: 'smooth' });
        }
      });

      return () => cancelAnimationFrame(rafId);
    }
  }, [currentSession?.messages]);

  const handleFileUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const newFiles = Array.from(files);
      setUploadedFiles(prev => [...prev, ...newFiles]);
    }
  }, []);

  const handleRemoveFile = useCallback((index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleSend = useCallback(async () => {
    if ((!input.trim() && uploadedFiles.length === 0) || !isMountedRef.current) {
      return;
    }
    
    // Allow sending when streaming for queueing functionality

    const message = input.trim();

    // Add message to history (avoid duplicates)
    setMessageHistory(prev => {
      const filtered = prev.filter(msg => msg !== message);
      return [message, ...filtered].slice(0, 50); // Keep last 50 messages
    });

    setInput('');
    setHistoryIndex(-1); // Reset history index

    // Mark tab as dirty when user sends a message
    if (tabId) {
      markTabDirty(tabId);
    }

    try {
      // If streaming, queue the message instead of sending immediately
      if (isStreaming) {
        queueMessage(message, uploadedFiles.length > 0 ? uploadedFiles : undefined);
        setUploadedFiles([]); // Clear files after queueing
        return;
      }

      // If files are uploaded, process them first
      if (uploadedFiles.length > 0) {
        setIsUploading(true);
        await sendMessage(message, uploadedFiles);
        setUploadedFiles([]); // Clear files after sending
      } else {
        await sendMessage(message);
      }

      // Mark tab as dirty again after AI response (in case it wasn't already)
      if (tabId && isMountedRef.current) {
        markTabDirty(tabId);
      }

      // Restore focus to input after AI response completes
      if (inputRef.current && isMountedRef.current && tabId === activeTabId) {
        setTimeout(() => {
          if (inputRef.current && isMountedRef.current) {
            inputRef.current.focus();
          }
        }, 200);
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      if (isMountedRef.current) {
        setError(error instanceof Error ? error.message : 'Failed to send message');

        // Restore focus even on error
        if (inputRef.current && tabId === activeTabId) {
          setTimeout(() => {
            if (inputRef.current && isMountedRef.current) {
              inputRef.current.focus();
            }
          }, 200);
        }
      }
    } finally {
      setIsUploading(false);
    }
  }, [input, uploadedFiles, isStreaming, tabId, markTabDirty, sendMessage, setError, activeTabId]);

  const handleKeyPress = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      // Always allow sending/queueing - the handleSend function will decide
      handleSend();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (messageHistory.length > 0) {
        const newIndex = Math.min(historyIndex + 1, messageHistory.length - 1);
        setHistoryIndex(newIndex);
        setInput(messageHistory[newIndex]);
      }
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (historyIndex > 0) {
        const newIndex = historyIndex - 1;
        setHistoryIndex(newIndex);
        setInput(messageHistory[newIndex]);
      } else if (historyIndex === 0) {
        setHistoryIndex(-1);
        setInput('');
      }
    }
  }, [handleSend, messageHistory, historyIndex, isStreaming]);

  // Focus input when this tab becomes active or after streaming ends
  useEffect(() => {
    if (tabId && activeTabId === tabId && inputRef.current && isMountedRef.current) {
      // Add a small delay to ensure the tab content is fully rendered
      const timeoutId = setTimeout(() => {
        if (inputRef.current && isMountedRef.current) {
          inputRef.current.focus();
        }
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [activeTabId, tabId, isStreaming]); // Added isStreaming dependency to refocus after AI response

  // Global keyboard handler to focus input when typing
  useEffect(() => {
    const handleGlobalKeyDown = (event: KeyboardEvent) => {
      // Only handle if this is the active tab and it's a workspace tab
      if (tabId !== activeTabId || !workspace) return;

      // Don't interfere with existing focus or system shortcuts
      const activeElement = document.activeElement as HTMLElement;
      const isInputFocused = activeElement?.tagName === 'INPUT' ||
        activeElement?.tagName === 'TEXTAREA' ||
        activeElement?.contentEditable === 'true';

      // Ignore system shortcuts and special keys (but allow arrow keys if input is focused for history navigation)
      if (event.ctrlKey || event.metaKey || event.altKey ||
        ['Tab', 'Enter', 'Escape'].includes(event.key) ||
        (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key) && !isInputFocused)) {
        return;
      }

      // If user starts typing and input isn't focused, focus it
      if (!isInputFocused && event.key.length === 1 && inputRef.current && isMountedRef.current) {
        inputRef.current.focus();
        // Don't prevent default - let the character be typed
      }
    };

    if (tabId === activeTabId) {
      document.addEventListener('keydown', handleGlobalKeyDown);
      return () => document.removeEventListener('keydown', handleGlobalKeyDown);
    }
  }, [activeTabId, tabId, workspace]);

  // Use ref to track component mount state
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleCommonActionClick = (prompt: string, title: string) => {
    if (isStreaming || !isMountedRef.current) return;

    // Set the input to the selected prompt
    setInput(prompt);

    // Optionally auto-send the prompt with timeout cleanup
    const timeoutId = setTimeout(() => {
      if (isMountedRef.current) {
        handleSend();
      }
    }, 100);

    // Store timeout ID for potential cleanup
    return () => clearTimeout(timeoutId);
  };

  return (
    <div className="chat-interface" style={{
      height: '100%',
      width: '100%',
      display: 'flex',
      flexDirection: 'row',
      overflow: 'hidden'
    }}>
      <div style={{
        flex: 2,
        height: '100%',
        minWidth: 0,
        padding: '0.5rem',
        backgroundColor: 'var(--color-bg-primary)' // Match workspace background
      }}>
        <div className="chat-container" style={{ height: '100%' }}>

          {/* Workspace context header */}
          {workspace && (
            <div className="workspace-title" style={{
              padding: '1rem',
              backgroundColor: 'var(--color-bg-primary)',
              borderRadius: '4px',
              marginBottom: '1rem'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '0.25rem'
              }}>
                <div style={{ marginTop: '0.125rem' }}>
                  <h3 style={{
                    margin: 0,
                    fontSize: '1.125rem',
                    fontWeight: '600',
                    color: 'var(--color-text-primary)'
                  }}>
                    {workspace.name}
                  </h3>
                  <p style={{
                    margin: '0.25rem 0 0 0',
                    fontSize: '0.875rem',
                    color: 'var(--color-text-secondary)',
                    fontStyle: 'italic'
                  }}>
                    {workspace.description}
                  </p>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.5rem', marginTop: '0.125rem' }}>
                  <span style={{
                    fontSize: '0.875rem',
                    color: 'var(--color-text-secondary)',
                    textTransform: 'capitalize'
                  }}>
                    {workspace.settings.domain} AI Assistant: <span style={{ fontWeight: 'bold' }}>{aiName}</span>
                  </span>
                  <ModelSelector
                    workspace={workspace}
                    sessionId={currentSession?.id}
                    onModelChange={(provider, model) => {
                      console.log(`Model changed to ${provider}/${model}`);
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Error notification */}
          {error && (
            <Alert
              type="error"
              message="Error"
              description={error}
              closable
              onClose={() => setError(null)}
              style={{ marginBottom: '1rem' }}
            />
          )}

          {/* Messages */}
          <div className="messages-container" style={{
            border: '1px solid var(--color-border-secondary)',
            borderRadius: 'var(--radius-md)',
            padding: '1rem',
            marginBottom: '1rem',
            backgroundColor: 'var(--color-bg-secondary)',
            minHeight: '300px',
            overflow: 'auto'
          }}>
            {currentSession?.messages.map((message) => (
              <ChatMessage key={message.id} message={message} />
            ))}


            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="chat-input">
            {/* Uploaded files display */}
            {uploadedFiles.length > 0 && (
              <div style={{
                marginBottom: '1rem',
                padding: '0.5rem',
                backgroundColor: 'var(--color-bg-secondary)',
                borderRadius: '4px',
                border: '1px solid var(--color-border-primary)',
              }}>
                <div style={{
                  fontSize: '0.75rem',
                  color: 'var(--color-text-secondary)',
                  marginBottom: '0.5rem',
                  fontWeight: '500'
                }}>
                  Attached files ({uploadedFiles.length}):
                </div>
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '0.25rem',
                }}>
                  {uploadedFiles.map((file, index) => (
                    <Tag
                      key={index}
                      closable
                      onClose={() => handleRemoveFile(index)}
                      title={`${file.name} (${(file.size / 1024).toFixed(1)}KB)`}
                      color="blue"
                    >
                      {file.name.length > 20 ? `${file.name.substring(0, 20)}...` : file.name}
                    </Tag>
                  ))}
                </div>
              </div>
            )}

            <div style={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: '0.5rem',
              maxWidth: '100%',
            }}>
              <div style={{ flex: 1 }}>
                <Input.TextArea
                  id="chat-input"
                  ref={inputRef}
                  placeholder={isStreaming ? "AI is thinking... You can continue typing..." : "Type your message..."}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyPress}
                  disabled={false}
                  rows={3}
                  style={{
                    resize: 'none',
                    borderRadius: '4px',
                    width: '100%',
                    fontSize: '1rem',
                  }}
                />
              </div>

              {/* File upload button */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                <input
                  type="file"
                  id="file-upload"
                  multiple
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                  accept=".txt,.md,.pdf,.doc,.docx,.csv,.json,.xml,.html,.py,.js,.ts,.jsx,.tsx,.css,.sql,.yaml,.yml,.log,.jpg,.jpeg,.png,.gif,.bmp,.webp,.svg"
                />
                <Button
                  type="default"
                  icon={<PaperClipOutlined />}
                  onClick={() => document.getElementById('file-upload')?.click()}
                  disabled={isStreaming || isUploading}
                  style={{
                    minHeight: '48px',
                    width: '48px',
                    flexShrink: 0,
                    borderRadius: '4px',
                  }}
                  title="Upload files"
                />
              </div>

              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={handleSend}
                loading={isUploading}
                disabled={(!input.trim() && uploadedFiles.length === 0)}
                style={{
                  minHeight: '48px',
                  flexShrink: 0,
                  borderRadius: '4px',
                }}
              >
                {isUploading ? 'Uploading...' : 
                 isStreaming ? 'Queue' : 'Send'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Combined Actions & Tools Panel - Made 32px wider */}
      <div style={{
        flex: 0.5, 
        height: '100%',
        minWidth: 320, // Made 32px wider (was ~288px)
        padding: '0.5rem',
        backgroundColor: 'var(--color-bg-primary)', // Match workspace background
        position: 'relative' // For logo positioning
      }}>
        {workspace ? (
          <div style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Common Actions */}
            <div style={{ marginBottom: '0.5rem' }}>
              <ErrorBoundary>
                <CommonActions
                  workspace={workspace}
                  onActionClick={handleCommonActionClick}
                />
              </ErrorBoundary>
            </div>

            {/* Tools & Data Sources */}
            <div style={{
              flex: 1,
              overflow: 'auto',
              minHeight: 0,
              paddingBottom: '80px' // Space for logo
            }}>
              <ErrorBoundary>
                <WorkspaceTools workspace={workspace} />
              </ErrorBoundary>
            </div>
          </div>
        ) : (
          <div style={{
            padding: '2rem',
            textAlign: 'center',
            color: 'var(--color-text-secondary)'
          }}>
            <p>No workspace selected</p>
          </div>
        )}
        
        {/* Silver Falls Capital Logo - Fixed to bottom right */}
        <div style={{
          position: 'absolute',
          bottom: '1rem',
          right: '1rem',
          width: '180px',
          height: 'auto',
          opacity: 0.6,
          transition: 'opacity 0.3s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.opacity = '1';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.opacity = '0.6';
        }}
        >
          <ThemeAwareLogo 
            alt="Silver Falls Capital" 
            style={{
              width: '100%',
              height: 'auto'
            }}
          />
        </div>
      </div>
    </div>
  );
};